import pouchdb from "pouchdb";
import { defineStore } from "pinia";
import { LOGGER } from "@/util/logger";
import pouchdbFind from "pouchdb-find";
import { RECORD_MANAGEMENT_TYPES } from "@/constants/StructureSearches";
import { useUserStore } from "@/stores/userStore";
import { ref, computed } from "vue";
import cryptoPouch from "crypto-pouch";
import { useEDocsIDBStore } from "@/stores/eDocsIDB";

export const useStructureIDBStore = defineStore("structureIDB", () => {
  const userStore = useUserStore();
  pouchdb.plugin(pouchdbFind);
  pouchdb.plugin(cryptoPouch);

  let localStructures = ref([]);
  let totalStructuresInIDB = ref(0);
  let dbInitialized = ref(false);

  const userName = computed(() => {
    return userStore.loggedInUser?.sub?.toLowerCase();
  });

  const dbName = computed(() => {
    return userName.value ? `bms-${userName.value}` : "";
  });

  const getLocalStructures = computed(() => {
    return localStructures.value;
  });

  let structuredb = ref(null);

  const initDB = async (isLoginAttempt) => {
    if (!dbInitialized.value || isLoginAttempt) {
      try {
        const db = pouchdb(dbName.value, {
          revs_limit: 0,
          auto_compaction: true,
        });

        const password = localStorage.getItem("hashedPass");
        if (password) {
          await db.crypto({
            password: password,
            ignore: "_attachments",
          });
        }
        await new Promise((resolve) => db.info(resolve));
        structuredb.value = db;
        dbInitialized.value = true;
      } catch (error) {
        LOGGER.logException(error);
      }
    }
  };

  const saveStructure = async (payload) => {
    LOGGER.logEvent(LOGGER.EventAction.Save, LOGGER.EventCat.Button, "IDB", payload);
    // eslint-disable-next-line no-unused-vars
    const { _attachments, EDocs, ...payloadWithNoAttachments } = payload;
    const eDocsIDBStore = useEDocsIDBStore();
    await eDocsIDBStore.initDB();

    try {
      let doc = await structuredb.value.get(payloadWithNoAttachments.structureBridge.brkey + "");

      const savedRecordType = doc.recordType;
      const updatedStructure = updateRelatedData(doc, payloadWithNoAttachments);
      updateIDBRecordType(savedRecordType, updatedStructure);
      if (doc.submittedTimestamp) {
        updatedStructure.submittedTimestamp = null;
      }
      await structuredb.value.put({
        ...updatedStructure,
      });
      await eDocsIDBStore.saveEDocsAndAttachments(payload);
    } catch (e) {
      let error = Object.assign(e, {
        method: "saveStructure",
        payload,
      });
      LOGGER.logException(error);
      updateIDBRecordType(null, payloadWithNoAttachments);

      await structuredb.value.put({
        _id: payload.structureBridge.brkey + "",
        ...payloadWithNoAttachments,
      });
      await eDocsIDBStore.saveEDocsAndAttachments(payload);
    }
    getStructuresFromIDB();
  };

  const updateRelatedData = (savedStructure, payload) => {
    if (payload.recordType === RECORD_MANAGEMENT_TYPES.BOTH) {
      //update structure/EDocs with payload data.
      return {
        ...savedStructure,
        ...payload,
        _rev: savedStructure._rev,
      };
    } else if (payload.recordType === RECORD_MANAGEMENT_TYPES.DOCUMENTS) {
      return {
        ...payload,
        ...savedStructure,
        EDocs: payload?.EDocs ?? [],
        _rev: savedStructure._rev,
        recordType: RECORD_MANAGEMENT_TYPES.DOCUMENTS,
      };
    } else {
      return {
        ...savedStructure,
        ...payload,
        EDocs: savedStructure?.EDocs ?? [],
        _rev: savedStructure._rev,
      };
    }
  };
  const deleteStructureFromIDB = async (payload) => {
    LOGGER.logEvent(LOGGER.EventAction.Delete, LOGGER.EventCat.Button, "IDB", payload);
    const eDocsIDBStore = useEDocsIDBStore();
    await eDocsIDBStore.initDB();
    for (const brKey of payload ?? []) {
      structuredb.value
        .get(brKey)
        .then(async function (doc) {
          await eDocsIDBStore.deleteEDocsFromIDB(brKey);
          return structuredb.value.remove(doc);
        })
        .then(() => {
          getStructuresFromIDB();
        })
        .catch((e) => {
          let error = Object.assign(e, {
            method: "deleteStructureFromIDB",
            payload,
          });
          LOGGER.logException(error);
        });
    }
  };
  const getStructuresFromIDB = async () => {
    clearStructuresFromState();
    await structuredb.value
      .allDocs({
        include_docs: true,
        attachments: false,
      })
      .then(function (result) {
        //filter user records and get structure records only
        localStructures.value = result?.rows
          ?.filter((doc) => {
            return !doc.doc.sub && !doc.doc.appPermissions;
          })
          ?.map((x) => x.doc);
        totalStructuresInIDB.value = localStructures.value?.length;
      })
      .catch(function (e) {
        let error = Object.assign(e, {
          method: "getStructuresFromIDB",
        });
        LOGGER.logException(error);
      });
  };

  const getDirtyStructuresFromIDB = async () => {
    return structuredb.value.find({
      selector: { backedUp: "N" },
    });
  };

  const getLatestInspectionFromIDB = async (brkey) => {
    try {
      let structures = await structuredb.value.find({
        selector: {
          _id: brkey + "",
        },
      });
      structures = structures.docs.sort((a, b) => {
        return new Date(b.inspection.inspectionDate) - new Date(a.inspection.inspectionDate);
      });
      return structures[0];
    } catch (e) {
      let error = Object.assign(e, {
        method: "getLatestInspectionFromIDB",
      });
      LOGGER.logException(error);
    }
  };
  const clearStructuresFromState = () => {
    localStructures.value = [];
  };
  const updateIDBRecordType = (savedRecordType, document) => {
    if (savedRecordType) {
      document.recordType = savedRecordType == document.recordType ? document.recordType : RECORD_MANAGEMENT_TYPES.BOTH;
    }
  };

  const getLatestInspectionKeyFromIDB = async (brkey) => {
    try {
      let structures = await structuredb.value.find({
        selector: {
          _id: brkey + "",
        },
        fields: ["inspection.inspectionId"],
      });
      return structures?.docs[0]?.inspection?.inspectionId;
    } catch (e) {
      let error = Object.assign(e, {
        method: "getLatestInspectionKeyFromIDB",
      });
      LOGGER.logException(error);
    }
  };
  const saveUserInfo = async (userInfo) => {
    LOGGER.logEvent(LOGGER.EventAction.Save, LOGGER.EventCat.Button, "SaveUserInfo", userInfo);

    try {
      let savedUser = await structuredb.value.get(userInfo?.sub);
      if (savedUser) {
        await structuredb.value.put({
          ...savedUser,
          ...userInfo,
        });
      }
    } catch (e) {
      let error = Object.assign(e, {
        method: "saveUserInfo",
        userInfo,
      });
      LOGGER.logException(error);

      await structuredb.value.put({
        _id: userInfo?.sub,
        ...userInfo,
      });
    }
  };

  const getUserInfo = async (userId) => {
    LOGGER.logEvent(LOGGER.EventAction.Search, LOGGER.EventCat.Button, "GetUserInfo", userId);
    try {
      const savedUser = await structuredb.value.get(userId);
      return savedUser;
    } catch (e) {
      let error = Object.assign(e, {
        method: "getUserInfo",
        userId,
      });
      LOGGER.logException(error);
    }
  };

  const saveUserAppPermissions = async (appPermissions) => {
    try {
      // Try to get existing document to preserve _rev
      const existing = await structuredb.value.get(`${userName.value}_app_permissions`).catch(() => null);

      await structuredb.value.put({
        _id: `${userName.value}_app_permissions`,
        ...(existing?._rev && { _rev: existing._rev }), // Include _rev if document exists
        appPermissions,
      });
    } catch (e) {
      LOGGER.logException(e);
    }
  };

  const getUserAppPermissions = async () => {
    try {
      const appPermissions = await structuredb.value.get(`${userName.value}_app_permissions`);
      return appPermissions;
    } catch (e) {
      let error = Object.assign(e, {
        method: "getUserAppPermissions",
        userId: userName.value,
      });
      LOGGER.logException(error);
    }
  };

  const updateStructures = async (structures) => {
    LOGGER.logEvent(LOGGER.EventAction.Update, LOGGER.EventCat.Button, "IDB", structures);

    try {
      await structuredb.value.bulkDocs(structures);
    } catch (e) {
      let error = Object.assign(e, {
        method: "updateStructures",
        structures,
      });
      LOGGER.logException(error);
    }
    getStructuresFromIDB();
  };

  const checkIfDBExists = async () => {
    try {
      const info = await structuredb.value.info();
      return info?.doc_count > 0;
    } catch (e) {
      LOGGER.logException(e);
      return false;
    }
  };

  const deleteDB = async (userId) => {
    await pouchdb(`bms-${userId}`)
      .destroy()
      .catch((err) => {
        LOGGER.logException(err);
      });
  };

  const loadDocumentAttachment = async (docId, attachmentName) => {
    const blob = await structuredb.value.getAttachment(docId, attachmentName);
    return { name: attachmentName, blob };
  };

  const processDocAttachments = async (doc) => {
    const attachments = doc._attachments;
    if (attachments) {
      const attachmentNames = Object.keys(attachments);
      const attachmentPromises = attachmentNames.map((name) => loadDocumentAttachment(doc._id, name));
      const loadedAttachments = await Promise.all(attachmentPromises);
      for (const { name, blob } of loadedAttachments ?? []) {
        doc._attachments[name].data = blob;
      }
    }
  };

  const prepareDocForExport = (row) => {
    const doc = row.doc;
    delete doc._rev;
    return doc;
  };

  const extractDocumentsFromDB = async () => {
    const docs = await structuredb.value.allDocs({
      include_docs: true,
      attachments: true,
    });

    const docsData = docs?.rows.map(prepareDocForExport);
    await Promise.all(docsData.map(processDocAttachments));

    return docsData;
  };

  const createNewEncryptedDB = async () => {
    const newDB = await pouchdb(`${dbName.value}-new`);
    LOGGER.logEvent(
      LOGGER.EventAction.OfflinePassword,
      LOGGER.EventCat.Button,
      "OfflinePassword",
      `New DB created, ${dbName.value}-new`,
    );
    return newDB;
  };

  const destroyOldDBAndCreateNew = async (newPassword) => {
    await structuredb.value.destroy();
    LOGGER.logEvent(LOGGER.EventAction.OfflinePassword, LOGGER.EventCat.Button, "OfflinePassword", "Destroyed old DB");
    localStorage.setItem("hashedPass", newPassword);
    //initialize DB with new password
    dbInitialized.value = false;
    await initDB(true);
    LOGGER.logEvent(
      LOGGER.EventAction.OfflinePassword,
      LOGGER.EventCat.Button,
      "OfflinePassword",
      "DB created with original name",
    );
  };

  const updatePassword = async (newPassword) => {
    try {
      const newDB = await createNewEncryptedDB();

      const docsData = await extractDocumentsFromDB();
      LOGGER.logEvent(
        LOGGER.EventAction.OfflinePassword,
        LOGGER.EventCat.Button,
        "OfflinePassword",
        "All docs extracted from old DB",
      );

      await newDB.bulkDocs(docsData);
      LOGGER.logEvent(
        LOGGER.EventAction.OfflinePassword,
        LOGGER.EventCat.Button,
        "OfflinePassword",
        "All docs loaded into new DB",
      );

      await destroyOldDBAndCreateNew(newPassword);

      LOGGER.logEvent(
        LOGGER.EventAction.OfflinePassword,
        LOGGER.EventCat.Button,
        "OfflinePassword",
        "Before loading all Docs from new DB to original named DB",
      );
      await newDB.replicate.to(structuredb.value);
      LOGGER.logEvent(
        LOGGER.EventAction.OfflinePassword,
        LOGGER.EventCat.Button,
        "OfflinePassword",
        "After loading all docs to original named DB",
      );

      await newDB.destroy();
      LOGGER.logEvent(
        LOGGER.EventAction.OfflinePassword,
        LOGGER.EventCat.Button,
        "OfflinePassword",
        "Destroyed temp DB",
      );
    } catch (e) {
      LOGGER.logException(e);
    }
  };

  return {
    localStructures,
    totalStructuresInIDB,
    getLocalStructures,
    saveStructure,
    updateRelatedData,
    deleteStructureFromIDB,
    getStructuresFromIDB,
    getDirtyStructuresFromIDB,
    getLatestInspectionFromIDB,
    clearStructuresFromState,
    updateIDBRecordType,
    getLatestInspectionKeyFromIDB,
    saveUserInfo,
    getUserInfo,
    updateStructures,
    checkIfDBExists,
    deleteDB,
    updatePassword,
    initDB,
    saveUserAppPermissions,
    getUserAppPermissions,
  };
});
